<template>
  <div class="fixed inset-0 z-50 overflow-hidden">
    <!-- Backdrop -->
    <Transition name="fade">
      <div
        v-show="localReport !== null"
        class="absolute inset-0 bg-gray-500 bg-opacity-50 fade-in"
        @click="$emit('close')"
      ></div>
    </Transition>

    <!-- Slide-over panel -->
    <Transition name="slide">
      <div
        v-if="localReport"
        class="absolute inset-y-0 right-0 max-w-[800px] w-full bg-white bg-opacity-50 shadow-xl slide-in pl-1 py-1 rounded-l-lg"
      >
        <div class="bg-white h-full flex flex-col px-4 py-2 rounded-l-md">
          <!-- Header -->
          <div class="py-3 px-2">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-3">
                <img
                  v-if="localReport.user_details?.avatar_image"
                  :src="localReport.user_details.avatar_image"
                  :alt="localReport.user_details.first_name"
                  class="h-10 w-10 rounded-full object-cover"
                />
                <div
                  v-else
                  class="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center"
                >
                  <User class="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <h2 class="text-lg font-medium text-gray-900">
                    {{ localReport.user_details?.first_name }}
                    {{ localReport.user_details?.last_name }}
                  </h2>
                  <p class="text-xs text-gray-500">
                    {{ localReport.user_details?.email }}
                  </p>
                </div>
              </div>
              <div class="flex items-center gap-4">
                <div class="flex gap-2">
                  <a
                    v-if="profileDetails?.linkedin_url"
                    :href="profileDetails.linkedin_url"
                    target="_blank"
                    class="hover:opacity-75"
                  >
                    <img src="/linkedin_logo.svg" class="w-5 h-5" alt="LinkedIn" />
                  </a>
                  <a
                    v-if="profileDetails?.telegram_handle"
                    :href="`https://t.me/${profileDetails.telegram_handle}`"
                    target="_blank"
                    class="hover:opacity-75"
                  >
                    <img src="/telegram_logo.svg" class="w-5 h-5" alt="Telegram" />
                  </a>
                  <a
                    v-if="profileDetails?.leetcode_url"
                    :href="profileDetails.leetcode_url"
                    target="_blank"
                    class="hover:opacity-75"
                  >
                    <img src="/leetcode_logo.svg" class="w-5 h-5" alt="LeetCode" />
                  </a>
                </div>
                <div class="flex gap-2">
                  <RouterLink
                    :to="`/profile/${localReport.user_details?.username}`"
                    target="_blank"
                    class="inline-flex items-center gap-1 px-3 py-1.5 border-[1px] border-gray-200 hover:bg-gray-50 hover:shadow-sm rounded-md text-xs text-gray-700 transition-colors"
                  >
                    <User class="w-4 h-4" />
                    View Profile
                  </RouterLink>
                  <button
                    class="p-1 rounded-md text-gray-400 hover:text-gray-500"
                    @click="$emit('close')"
                  >
                    <X class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="flex-1 overflow-y-auto">
            <!-- Current Report Section -->
            <ReportCard
              :report="localReport"
              :show-actions="true"
              @report-updated="handleReportUpdated"
            />
            <hr v-if="otherReports.length > 0" class="my-4" />

            <!-- Other Reports Section -->
            <div v-if="otherReports.length > 0" class="mb-4">
              <div>
                <button
                  @click="isOtherReportsExpanded = !isOtherReportsExpanded"
                  class="w-full text-left text-sm font-medium text-gray-900 mb-3 flex items-center hover:text-gray-700 transition-colors"
                >
                  <component
                    :is="isOtherReportsExpanded ? ChevronDown : ChevronRight"
                    class="w-4 h-4 mr-1"
                  />
                  <FileText class="w-4 h-4 mr-1" />
                  Other Reports and Warnings for {{ localReport.user_details?.first_name }}
                </button>
                <div
                  v-show="isOtherReportsExpanded"
                  class="space-y-4 bg-gray-50 rounded-lg border-[1px] border-gray-200 p-4 fade-in"
                >
                  <ReportsList
                    :reports="otherReports"
                    :show-actions="true"
                    @report-updated="handleReportUpdated"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import ReportCard from '@/components/manage/ReportCard.vue'
import {
  FileText,
  ChevronDown,
  ChevronRight,
  User,
  X,
} from 'lucide-vue-next'
import type { Report, FellowReport } from '@/types/reports'
import type { User as UserType } from '@/types/user'
import type { ProfileDetails } from '@/types/userProfile'
import ReportsList from '@/components/shared/ReportsList.vue'
import { RouterLink } from 'vue-router'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

const props = defineProps<{
  selectedReport: Report | FellowReport | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'navigate-to-report', report: Report | FellowReport): void
  (e: 'report-updated', report: Report | FellowReport): void
}>()

// Create a local copy of the report that we can mutate
const localReport = computed(() => (props.selectedReport ? { ...props.selectedReport } : null))

// Create a computed property for profile details
const profileDetails = computed<ProfileDetails | null>(() => {
  if (!localReport.value) return null
  const userDetails = localReport.value.user_details as UserType
  return {
    user_details: {
      username: userDetails.username,
      first_name: userDetails.first_name,
      last_name: userDetails.last_name,
      email: userDetails.email,
      avatar_image: userDetails.avatar_image || ''
    },
    linkedin_url: userDetails.linkedin_profile_url || undefined,
    telegram_handle: userDetails.telegram_username || undefined,
    leetcode_url: userDetails.leetcode_profile_url || undefined
  }
})

const handleReportUpdated = (updatedReport: Report | FellowReport) => {
  emit('report-updated', updatedReport)
}

// Update the otherReports to be a ref instead of computed
const otherReports = ref<(Report | FellowReport)[]>([])

// Add a function to fetch other reports
const fetchOtherReports = async () => {
  const report = localReport.value
  if (!report?.user_details?.username) return

  try {
    const response = await api.get(API_ROUTES.GET_USER_FELLOW_REPORTS(report.user_details.username))
    // Filter out the current report from the list
    otherReports.value = response.data.filter((r: Report | FellowReport) => r.id !== report.id)
  } catch (error) {
    console.error('Error fetching other reports:', error)
    otherReports.value = []
  }
}

// Watch for changes in the selected report
watch(
  () => props.selectedReport,
  (newReport) => {
    if (newReport) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchOtherReports()
    } else {
      otherReports.value = []
    }
  }
)

// Fetch other reports when component mounts if there's a selected report
onMounted(() => {
  if (props.selectedReport) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchOtherReports()
  }
})

// Add state for other reports section expansion
const isOtherReportsExpanded = ref(false)
</script>

<style scoped>
/* Fade transition for backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide transition for panel */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-out;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}

/* Fade in animation */
.fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Slide in animation */
.slide-in {
  animation: slideIn 0.2s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
</style>
